import { useRef, useState } from "react";
import { toast } from "sonner";
import { api, formatApiError, mediaUrl } from "@/lib/api";
import { Upload, Trash2, Eye, EyeOff, GripVertical } from "lucide-react";

const SLOTS = [
  { id: "fullscreen", title: "Vollbild-Werbung", subtitle: "Läuft im Modus „Werbung“ als Vollbild (1920×1080)." },
  { id: "banner", title: "Banner unten", subtitle: "Untere Sponsorenleiste im Anzeigetafel-Modus." },
  { id: "logo", title: "Logo oben rechts", subtitle: "Logo-/Sponsorenfeld oben rechts im Anzeigetafel-Modus." },
];

function MediaSection({ slot, title, subtitle, items, defaultDur }) {
  const fileRef = useRef();
  const [uploading, setUploading] = useState(false);
  const [dragId, setDragId] = useState(null);
  const [overId, setOverId] = useState(null);

  const getDur = (file) => new Promise((res) => {
    if (!file.type.startsWith("video")) return res(defaultDur);
    const v = document.createElement("video");
    v.preload = "metadata";
    v.onloadedmetadata = () => { URL.revokeObjectURL(v.src); res(Math.max(1, Math.round(v.duration || defaultDur))); };
    v.onerror = () => res(defaultDur);
    v.src = URL.createObjectURL(file);
  });

  const onUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setUploading(true);
    try {
      for (const file of files) {
        const dur = await getDur(file);
        const fd = new FormData();
        fd.append("file", file);
        fd.append("duration", String(dur));
        fd.append("slot", slot);
        await api.post("/media", fd, { headers: { "Content-Type": "multipart/form-data" } });
      }
      toast.success("Hochgeladen");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };
  const update = async (id, patch) => {
    try { await api.put(`/media/${id}`, patch); } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const remove = async (id) => { await api.delete(`/media/${id}`); toast.success("Entfernt"); };
  const onDrop = async (targetId) => {
    if (!dragId || dragId === targetId) { setDragId(null); setOverId(null); return; }
    const arr = [...items];
    const from = arr.findIndex((m) => m.id === dragId);
    const to = arr.findIndex((m) => m.id === targetId);
    if (from === -1 || to === -1) return;
    const [mv] = arr.splice(from, 1);
    arr.splice(to, 0, mv);
    setDragId(null); setOverId(null);
    await api.post("/media/reorder", { ordered_ids: arr.map((m) => m.id) });
  };

  return (
    <section className="bg-[#121620] border border-[#1E2638] rounded-2xl p-4 sm:p-6">
      <div className="flex items-start justify-between gap-3 mb-4 flex-wrap">
        <div>
          <h2 className="font-display font-bold uppercase tracking-wider text-lg">{title}</h2>
          <p className="text-xs text-[#8A99AD] mt-0.5">{subtitle}</p>
        </div>
        <input ref={fileRef} type="file" accept="image/png,image/jpeg,image/webp,image/gif,image/svg+xml,video/mp4,application/pdf" multiple onChange={onUpload} className="hidden" data-testid={`media-file-input-${slot}`} />
        <button onClick={() => fileRef.current?.click()} disabled={uploading} data-testid={`media-upload-btn-${slot}`}
          className="flex items-center gap-2 bg-[#E5FF00] text-black font-display font-bold uppercase tracking-wider text-sm px-4 py-2 rounded-lg hover:bg-white transition-colors disabled:opacity-50 shrink-0">
          <Upload className="w-4 h-4" /> {uploading ? "…" : "Hochladen"}
        </button>
      </div>
      <div className="space-y-2">
        {items.map((m, i) => (
          <div key={m.id} draggable onDragStart={() => setDragId(m.id)} onDragOver={(e) => { e.preventDefault(); setOverId(m.id); }} onDragEnd={() => { setDragId(null); setOverId(null); }} onDrop={() => onDrop(m.id)}
            className={`flex items-center gap-2 sm:gap-3 bg-[#0A0C10] border rounded-lg p-2 ${overId === m.id && dragId !== m.id ? "border-[#E5FF00]" : "border-[#1E2638]"} ${dragId === m.id ? "opacity-40" : ""}`} data-testid={`media-item-${m.id}`}>
            <span className="text-[#8A99AD] cursor-grab active:cursor-grabbing shrink-0" data-testid={`media-drag-${m.id}`}><GripVertical className="w-5 h-5" /></span>
            <span className="font-mono-num text-[#8A99AD] w-4 text-center text-sm shrink-0">{i + 1}</span>
            <div className="w-14 h-10 rounded overflow-hidden bg-black flex items-center justify-center shrink-0">
              {m.type === "video" ? <video src={mediaUrl(m.id)} className="w-full h-full object-cover" muted /> : m.type === "pdf" ? <div className="text-[9px] text-[#8A99AD]">PDF</div> : <img src={mediaUrl(m.id)} alt="" className="w-full h-full object-cover" />}
            </div>
            <div className="flex-1 min-w-0"><p className="text-sm truncate">{m.original_name}</p><p className="text-[10px] text-[#8A99AD] uppercase tracking-wider">{m.type}</p></div>
            <label className="flex items-center gap-1 text-xs text-[#8A99AD] shrink-0">
              <input type="number" defaultValue={m.duration} onBlur={(e) => update(m.id, { duration: Number(e.target.value) })} data-testid={`media-duration-${m.id}`} className="w-12 sm:w-14 bg-[#121620] border border-[#1E2638] rounded px-1 sm:px-2 py-1.5 text-center font-mono-num outline-none" />s
            </label>
            <button onClick={() => update(m.id, { active: !m.active })} data-testid={`media-toggle-${m.id}`} className={`p-2 rounded-lg border shrink-0 ${m.active ? "border-[#00F0FF] text-[#00F0FF]" : "border-[#1E2638] text-[#8A99AD]"}`}>{m.active ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}</button>
            <button onClick={() => remove(m.id)} data-testid={`media-delete-${m.id}`} className="p-2 rounded-lg text-[#8A99AD] hover:text-[#FF2A55] shrink-0"><Trash2 className="w-4 h-4" /></button>
          </div>
        ))}
        {items.length === 0 && <p className="text-[#8A99AD] text-sm">Noch keine Medien.</p>}
      </div>
    </section>
  );
}

export default function MediaTab({ snapshot }) {
  const media = snapshot.media || [];
  const defaultDur = snapshot.settings.default_media_duration || 10;
  const bySlot = (slot) => media.filter((m) => (m.slot || "fullscreen") === slot).sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
  return (
    <div className="space-y-6">
      {SLOTS.map((s) => (
        <MediaSection key={s.id} slot={s.id} title={s.title} subtitle={s.subtitle} items={bySlot(s.id)} defaultDur={defaultDur} />
      ))}
    </div>
  );
}
